/**
 * Textos de presentación de la regularización (007 · T034–T036; contracts/ui.md §1).
 *
 * Solo **formatean** lo que manda el servidor: el ancla se lee con su `site_timezone` (la de la
 * marca, wiring 007 §0.5) y los límites de la propuesta con `bounds.time_zone` (la vigente de la
 * sede, en la que el trabajador escribe). Ninguna función decide si hay hueco o si una hora vale.
 */
import type {
  AttendanceAction,
  StaffProposalBounds,
  StaffRegularizationAnchor,
  StaffRegularizationItem,
} from '@fermin/contracts';

import { formatTime, LOCALE, t } from '../../i18n';

const LOCAL_DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MINUTE_MS = 60_000;

/** `YYYY-MM-DD` → mediodía UTC (a salvo de corrimientos de zona). */
function dayAsUtcNoon(day: string): Date | null {
  const match = LOCAL_DAY_PATTERN.exec(day);
  if (!match) {
    return null;
  }
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
}

/** "martes 23" para un día operativo `YYYY-MM-DD` (el día ya viene calculado por el servidor). */
export function shortDay(day: string): string {
  const date = dayAsUtcNoon(day);
  if (!date) {
    return day;
  }
  return new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', timeZone: 'UTC' })
    .format(date)
    .replace(',', '');
}

/** "Martes 23" (segmentos del selector de día y títulos de fila). */
export function shortDayTitle(day: string): string {
  const text = shortDay(day);
  return text.charAt(0).toLocaleUpperCase(LOCALE) + text.slice(1);
}

/** "salida"/"entrada" (en minúscula, dentro de una frase). */
export function missingNoun(type: AttendanceAction): string {
  return t(`regularization.missing.${type}`);
}

/** "Salida"/"Entrada". */
export function typeLabel(type: AttendanceAction): string {
  return t(`regularization.type.${type}`);
}

/** "Entrada 08:57 · Angamos" (o con "· martes 23" al final). */
export function anchorSummary(anchor: StaffRegularizationAnchor, withDay = false): string {
  const vars = {
    type: typeLabel(anchor.type),
    time: formatTime(anchor.server_timestamp, anchor.site_timezone),
    site: anchor.site.name,
    day: shortDay(anchor.operative_day),
  };
  return withDay
    ? t('regularization.anchorSummaryWithDay', vars)
    : t('regularization.anchorSummary', vars);
}

/**
 * Ayuda del campo de hora: "Entre las 08:58 y las 00:57", en `bounds.time_zone`. `not_before` se
 * muestra como el primer minuto entero **posterior** (el extremo es exclusivo frente al ancla) y
 * `not_after` como su propio minuto. Es solo orientación: la validación final es del servidor
 * (errores por campo), que conoce la inclusividad exacta de cada extremo.
 */
export function boundsHelp(bounds: StaffProposalBounds): string {
  const notBefore = new Date(bounds.not_before).getTime();
  const notAfter = new Date(bounds.not_after).getTime();
  const first = Math.floor(notBefore / MINUTE_MS) * MINUTE_MS + MINUTE_MS;
  const last = Math.floor(notAfter / MINUTE_MS) * MINUTE_MS;
  return t('regularization.form.timeHelp', {
    from: formatTime(new Date(first).toISOString(), bounds.time_zone),
    to: formatTime(new Date(last).toISOString(), bounds.time_zone),
  });
}

/**
 * Día preseleccionado del formulario: el del ancla si está entre los permitidos; si no, el más
 * cercano en la dirección contraria al hueco (el primero para una salida, el último para una
 * entrada).
 */
export function defaultProposalDay(item: StaffRegularizationItem, bounds: StaffProposalBounds): string {
  if (bounds.allowed_days.includes(item.anchor.operative_day)) {
    return item.anchor.operative_day;
  }
  return item.missing_type === 'CHECK_OUT'
    ? bounds.allowed_days[0]!
    : bounds.allowed_days[bounds.allowed_days.length - 1]!;
}
